import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import styles from './NetworkVisualization.module.css';

const COLORS = {
  gold: 0xffc700,
  critical: 0xff4757,
  warning: 0xffa502,
  normal: 0x2ed573,
  blue: 0x3742fa,
  darkBlue: 0x003566,
  gridLine: 0x004477
};

const NetworkVisualization: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const animationRef = useRef<number>();

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    let isCleanedUp = false;

    // Small delay to ensure container has dimensions
    const initTimeout = setTimeout(() => {
      if (isCleanedUp) return;

      const width = container.clientWidth || 800;
      const height = container.clientHeight || 500;

      // Scene setup
      const scene = new THREE.Scene();
      scene.fog = new THREE.FogExp2(0x003566, 0.012);

      const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
      camera.position.set(0, 35, 50);
      camera.lookAt(0, 0, 0);

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setClearColor(0x003566, 1);
      rendererRef.current = renderer;

      // Remove any existing canvas
      const existingCanvas = container.querySelector('canvas');
      if (existingCanvas) existingCanvas.remove();

      // Append the new canvas
      container.appendChild(renderer.domElement);

      // Create terrain
      function createTerrain() {
        const gridSize = 100;
        const divisions = 40;

        const gridGeometry = new THREE.PlaneGeometry(gridSize, gridSize, divisions, divisions);
        const positions = gridGeometry.attributes.position.array as Float32Array;

        for (let i = 0; i < positions.length; i += 3) {
          const x = positions[i];
          const z = positions[i + 1];
          positions[i + 2] = Math.sin(x * 0.1) * Math.cos(z * 0.1) * 2;
        }

        gridGeometry.computeVertexNormals();

        const gridMaterial = new THREE.MeshBasicMaterial({
          color: COLORS.gridLine,
          wireframe: true,
          transparent: true,
          opacity: 0.3
        });

        const grid = new THREE.Mesh(gridGeometry, gridMaterial);
        grid.rotation.x = -Math.PI / 2;
        grid.position.y = -5;
        scene.add(grid);

        const planeGeometry = new THREE.PlaneGeometry(gridSize, gridSize);
        const planeMaterial = new THREE.MeshBasicMaterial({
          color: 0x002244,
          transparent: true,
          opacity: 0.8
        });
        const plane = new THREE.Mesh(planeGeometry, planeMaterial);
        plane.rotation.x = -Math.PI / 2;
        plane.position.y = -5.1;
        scene.add(plane);

        return grid;
      }

      // Network Node class
      class NetworkNode {
        position: THREE.Vector3;
        status: string;
        size: number;
        pulsePhase: number;
        core: THREE.Mesh;
        ring: THREE.Mesh;
        label: THREE.Mesh;
        beam?: THREE.Mesh;

        constructor(position: THREE.Vector3, status: string, size = 1) {
          this.position = position;
          this.status = status;
          this.size = size;
          this.pulsePhase = Math.random() * Math.PI * 2;

          const color = status === 'critical' ? COLORS.critical :
                        status === 'warning' ? COLORS.warning :
                        status === 'normal' ? COLORS.normal : COLORS.gold;

          const coreGeometry = new THREE.SphereGeometry(size * 0.5, 16, 16);
          const coreMaterial = new THREE.MeshBasicMaterial({
            color: color,
            transparent: true,
            opacity: 0.9
          });
          this.core = new THREE.Mesh(coreGeometry, coreMaterial);
          this.core.position.copy(position);

          const ringGeometry = new THREE.RingGeometry(size * 0.8, size * 1.2, 32);
          const ringMaterial = new THREE.MeshBasicMaterial({
            color: color,
            transparent: true,
            opacity: 0.3,
            side: THREE.DoubleSide
          });
          this.ring = new THREE.Mesh(ringGeometry, ringMaterial);
          this.ring.position.copy(position);
          this.ring.rotation.x = -Math.PI / 2;

          if (status === 'critical') {
            const beamGeometry = new THREE.CylinderGeometry(0.1, 0.1, 30, 8);
            const beamMaterial = new THREE.MeshBasicMaterial({
              color: COLORS.critical,
              transparent: true,
              opacity: 0.2
            });
            this.beam = new THREE.Mesh(beamGeometry, beamMaterial);
            this.beam.position.copy(position);
            this.beam.position.y += 15;
          }

          const labelGeometry = new THREE.PlaneGeometry(3, 0.8);
          const labelCanvas = document.createElement('canvas');
          labelCanvas.width = 256;
          labelCanvas.height = 64;
          const ctx = labelCanvas.getContext('2d')!;
          ctx.fillStyle = 'rgba(10, 14, 26, 0.8)';
          ctx.beginPath();
          ctx.roundRect(0, 0, 256, 64, 8);
          ctx.fill();
          ctx.fillStyle = status === 'critical' ? '#ff4757' :
                         status === 'warning' ? '#ffa502' : '#2ed573';
          ctx.font = 'bold 24px sans-serif';
          ctx.fillText(`Node ${Math.floor(Math.random() * 900) + 100}`, 16, 40);

          const labelTexture = new THREE.CanvasTexture(labelCanvas);
          const labelMaterial = new THREE.MeshBasicMaterial({
            map: labelTexture,
            transparent: true,
            opacity: 0.9
          });
          this.label = new THREE.Mesh(labelGeometry, labelMaterial);
          this.label.position.copy(position);
          this.label.position.y += size * 2;
        }

        addToScene(s: THREE.Scene) {
          s.add(this.core);
          s.add(this.ring);
          s.add(this.label);
          if (this.beam) s.add(this.beam);
        }

        update(time: number, cam: THREE.Camera) {
          const pulse = Math.sin(time * 2 + this.pulsePhase) * 0.5 + 0.5;
          this.ring.scale.setScalar(1 + pulse * 0.3);
          (this.ring.material as THREE.MeshBasicMaterial).opacity = 0.3 - pulse * 0.2;
          this.core.position.y = this.position.y + Math.sin(time + this.pulsePhase) * 0.3;
          this.ring.position.y = this.core.position.y;
          this.label.position.y = this.core.position.y + this.size * 2;
          this.label.lookAt(cam.position);
        }
      }

      // Data Stream class
      class DataStream {
        startNode: NetworkNode;
        endNode: NetworkNode;
        particleCount: number;
        geometry: THREE.BufferGeometry;
        points: THREE.Points;
        line: THREE.Line;
        offset: number;

        constructor(startNode: NetworkNode, endNode: NetworkNode) {
          this.startNode = startNode;
          this.endNode = endNode;
          this.particleCount = 15;
          this.offset = Math.random();

          const positions = new Float32Array(this.particleCount * 3);
          const colors = new Float32Array(this.particleCount * 3);
          const color = new THREE.Color(COLORS.gold);

          for (let i = 0; i < this.particleCount; i++) {
            colors[i * 3] = color.r;
            colors[i * 3 + 1] = color.g;
            colors[i * 3 + 2] = color.b;
          }

          this.geometry = new THREE.BufferGeometry();
          this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
          this.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

          const material = new THREE.PointsMaterial({
            size: 0.3,
            vertexColors: true,
            transparent: true,
            opacity: 0.8,
            blending: THREE.AdditiveBlending
          });

          this.points = new THREE.Points(this.geometry, material);

          const lineGeometry = new THREE.BufferGeometry();
          const linePositions = new Float32Array(6);
          lineGeometry.setAttribute('position', new THREE.BufferAttribute(linePositions, 3));

          const lineMaterial = new THREE.LineBasicMaterial({
            color: COLORS.gold,
            transparent: true,
            opacity: 0.1
          });

          this.line = new THREE.Line(lineGeometry, lineMaterial);
        }

        addToScene(s: THREE.Scene) {
          s.add(this.points);
          s.add(this.line);
        }

        update(time: number) {
          const start = this.startNode.core.position;
          const end = this.endNode.core.position;

          const linePositions = this.line.geometry.attributes.position.array as Float32Array;
          linePositions[0] = start.x;
          linePositions[1] = start.y;
          linePositions[2] = start.z;
          linePositions[3] = end.x;
          linePositions[4] = end.y;
          linePositions[5] = end.z;
          this.line.geometry.attributes.position.needsUpdate = true;

          const positions = this.geometry.attributes.position.array as Float32Array;

          for (let i = 0; i < this.particleCount; i++) {
            const t = ((time * 0.5 + this.offset + i / this.particleCount) % 1);
            const midPoint = new THREE.Vector3().lerpVectors(start, end, 0.5);
            midPoint.y += start.distanceTo(end) * 0.2;

            const t1 = 1 - t;
            positions[i * 3] = t1 * t1 * start.x + 2 * t1 * t * midPoint.x + t * t * end.x;
            positions[i * 3 + 1] = t1 * t1 * start.y + 2 * t1 * t * midPoint.y + t * t * end.y;
            positions[i * 3 + 2] = t1 * t1 * start.z + 2 * t1 * t * midPoint.z + t * t * end.z;
          }

          this.geometry.attributes.position.needsUpdate = true;
        }
      }

      // Create ambient particles
      function createAmbientParticles() {
        const particleCount = 500;
        const positions = new Float32Array(particleCount * 3);
        const colors = new Float32Array(particleCount * 3);

        const goldColor = new THREE.Color(COLORS.gold);
        const blueColor = new THREE.Color(COLORS.blue);

        for (let i = 0; i < particleCount; i++) {
          positions[i * 3] = (Math.random() - 0.5) * 100;
          positions[i * 3 + 1] = Math.random() * 40;
          positions[i * 3 + 2] = (Math.random() - 0.5) * 100;

          const color = Math.random() > 0.7 ? goldColor : blueColor;
          colors[i * 3] = color.r;
          colors[i * 3 + 1] = color.g;
          colors[i * 3 + 2] = color.b;
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

        const material = new THREE.PointsMaterial({
          size: 0.15,
          vertexColors: true,
          transparent: true,
          opacity: 0.6,
          blending: THREE.AdditiveBlending
        });

        const particles = new THREE.Points(geometry, material);
        scene.add(particles);

        return { geometry, particles };
      }

      // Create hex platform
      function createHexPlatform() {
        const hexShape = new THREE.Shape();
        const size = 45;

        for (let i = 0; i < 6; i++) {
          const angle = (i / 6) * Math.PI * 2 - Math.PI / 2;
          const x = Math.cos(angle) * size;
          const y = Math.sin(angle) * size;
          if (i === 0) hexShape.moveTo(x, y);
          else hexShape.lineTo(x, y);
        }
        hexShape.closePath();

        const points = hexShape.getPoints(6);
        const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
        const lineMaterial = new THREE.LineBasicMaterial({
          color: COLORS.gold,
          transparent: true,
          opacity: 0.3
        });
        const hexLine = new THREE.LineLoop(lineGeometry, lineMaterial);
        hexLine.rotation.x = -Math.PI / 2;
        hexLine.position.y = -4.9;
        scene.add(hexLine);

        for (let r = 15; r <= 35; r += 10) {
          const ringGeometry = new THREE.RingGeometry(r - 0.1, r + 0.1, 64);
          const ringMaterial = new THREE.MeshBasicMaterial({
            color: COLORS.gold,
            transparent: true,
            opacity: 0.1,
            side: THREE.DoubleSide
          });
          const ring = new THREE.Mesh(ringGeometry, ringMaterial);
          ring.rotation.x = -Math.PI / 2;
          ring.position.y = -4.8;
          scene.add(ring);
        }
      }

      // Create central hub
      function createCentralHub() {
        const hubGeometry = new THREE.IcosahedronGeometry(3, 1);
        const hubMaterial = new THREE.MeshBasicMaterial({
          color: COLORS.gold,
          wireframe: true,
          transparent: true,
          opacity: 0.8
        });
        const hub = new THREE.Mesh(hubGeometry, hubMaterial);
        hub.position.y = 5;
        scene.add(hub);

        const coreGeometry = new THREE.IcosahedronGeometry(1.5, 0);
        const coreMaterial = new THREE.MeshBasicMaterial({
          color: COLORS.gold,
          transparent: true,
          opacity: 0.9
        });
        const core = new THREE.Mesh(coreGeometry, coreMaterial);
        core.position.y = 5;
        scene.add(core);

        const rings: THREE.Mesh[] = [];
        const ringRadii = [4, 5.5, 7];

        ringRadii.forEach((radius, i) => {
          const ringGeometry = new THREE.TorusGeometry(radius, 0.05, 8, 64);
          const ringMaterial = new THREE.MeshBasicMaterial({
            color: COLORS.gold,
            transparent: true,
            opacity: 0.4
          });
          const ring = new THREE.Mesh(ringGeometry, ringMaterial);
          ring.position.y = 5;
          ring.rotation.x = Math.PI / 2 + (i * 0.3);
          ring.rotation.z = i * 0.5;
          rings.push(ring);
          scene.add(ring);
        });

        return { hub, core, rings };
      }

      // Initialize scene
      const terrain = createTerrain();
      createHexPlatform();
      const centralHub = createCentralHub();
      const ambientParticles = createAmbientParticles();

      // Create nodes
      const nodes: NetworkNode[] = [];
      const nodePositions = [
        { pos: new THREE.Vector3(-25, 0, -15), status: 'critical' },
        { pos: new THREE.Vector3(-20, 0, 10), status: 'warning' },
        { pos: new THREE.Vector3(-10, 0, -25), status: 'normal' },
        { pos: new THREE.Vector3(5, 0, -20), status: 'normal' },
        { pos: new THREE.Vector3(25, 0, -10), status: 'warning' },
        { pos: new THREE.Vector3(20, 0, 15), status: 'critical' },
        { pos: new THREE.Vector3(10, 0, 25), status: 'normal' },
        { pos: new THREE.Vector3(-15, 0, 20), status: 'normal' },
        { pos: new THREE.Vector3(-30, 0, 0), status: 'warning' },
        { pos: new THREE.Vector3(30, 0, 5), status: 'normal' },
        { pos: new THREE.Vector3(0, 0, -30), status: 'critical' },
        { pos: new THREE.Vector3(0, 0, 30), status: 'warning' },
      ];

      nodePositions.forEach(({ pos, status }) => {
        const node = new NetworkNode(pos, status, 1);
        node.addToScene(scene);
        nodes.push(node);
      });

      // Create data streams
      const streams: DataStream[] = [];
      const connections = [
        [0, 1], [1, 7], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7],
        [0, 2], [4, 9], [2, 10], [6, 11], [8, 0], [8, 1],
        [9, 5], [10, 3], [11, 7]
      ];

      connections.forEach(([a, b]) => {
        const stream = new DataStream(nodes[a], nodes[b]);
        stream.addToScene(scene);
        streams.push(stream);
      });

      // Animation loop
      let time = 0;

      function animate() {
        if (isCleanedUp) return;
        animationRef.current = requestAnimationFrame(animate);
        time += 0.016;

        // Camera orbit
        const cameraRadius = 55;
        const cameraSpeed = 0.05;
        camera.position.x = Math.sin(time * cameraSpeed) * cameraRadius;
        camera.position.z = Math.cos(time * cameraSpeed) * cameraRadius;
        camera.position.y = 30 + Math.sin(time * 0.1) * 5;
        camera.lookAt(0, 5, 0);

        // Update nodes
        nodes.forEach(node => node.update(time, camera));

        // Update data streams
        streams.forEach(stream => stream.update(time));

        // Rotate central hub
        centralHub.hub.rotation.y += 0.005;
        centralHub.hub.rotation.x += 0.002;
        centralHub.core.rotation.y -= 0.01;
        centralHub.core.rotation.x -= 0.005;

        centralHub.rings.forEach((ring, i) => {
          ring.rotation.z += 0.003 * (i + 1);
          ring.rotation.x += 0.001 * (i + 1);
        });

        // Animate ambient particles
        const positions = ambientParticles.geometry.attributes.position.array as Float32Array;
        for (let i = 0; i < positions.length; i += 3) {
          positions[i + 1] += Math.sin(time + positions[i]) * 0.01;
          if (positions[i + 1] > 40) positions[i + 1] = 0;
        }
        ambientParticles.geometry.attributes.position.needsUpdate = true;

        // Subtle terrain animation
        const terrainPositions = terrain.geometry.attributes.position.array as Float32Array;
        for (let i = 0; i < terrainPositions.length; i += 3) {
          const x = terrainPositions[i];
          const z = terrainPositions[i + 1];
          terrainPositions[i + 2] = Math.sin(x * 0.1 + time * 0.5) * Math.cos(z * 0.1 + time * 0.3) * 2;
        }
        terrain.geometry.attributes.position.needsUpdate = true;

        renderer.render(scene, camera);
      }

      // Handle resize
      const handleResize = () => {
        if (!container || isCleanedUp) return;
        const newWidth = container.clientWidth;
        const newHeight = container.clientHeight;
        camera.aspect = newWidth / newHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(newWidth, newHeight);
      };

      window.addEventListener('resize', handleResize);
      animate();

    }, 100);

    // Cleanup
    return () => {
      isCleanedUp = true;
      clearTimeout(initTimeout);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
    };
  }, []);

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.overlay}>
        <div className={styles.gridOverlay}></div>
        <div className={styles.scanLine}></div>
        <div className={styles.vignette}></div>
      </div>
    </div>
  );
};

export default NetworkVisualization;
